import React, { useState, useEffect, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useDispatch, useSelector } from 'react-redux';
import { doPasswordReset, resetNotification} from './authSlice';

// ─── Design tokens (mirrored exactly from LoginPage) ─────────────────────────
const tokens = {
  navy:        "#0A1628",
  accent:      "#2563EB",
  accentHover: "#1D4ED8",
  border:      "#E2E8F0",
  surface:     "#F8FAFC",
  white:       "#FFFFFF",
  danger:      "#DC2626",
  textPrimary:   "#0F172A",
  textSecondary: "#475569",
};

// ─── Global styles (mirrored exactly from LoginPage) ──────────────────────────
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

    *, *::before, *::after { box-sizing: border-box; }

    .reset-root {
      display: flex;
      min-height: 100vh;
      font-family: 'Inter', system-ui, sans-serif;
      background: ${tokens.surface};
    }

    /* ── Left panel (identical to LoginPage) ── */
    .reset-panel {
      width: 380px;
      flex-shrink: 0;
      background: ${tokens.navy};
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      padding: 48px 40px;
      position: relative;
      overflow: hidden;
    }
    .reset-panel::before {
      content: '';
      position: absolute;
      inset: 0;
      background:
        radial-gradient(ellipse 600px 500px at -100px 120%, #2563EB22 0%, transparent 70%),
        radial-gradient(ellipse 400px 400px at 420px 500px, #2563EB18 0%, transparent 70%);
      pointer-events: none;
    }
    .reset-panel-logo img {
      width:170px;
    }
    .reset-panel-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      background: rgba(37,99,235,0.18);
      border: 1px solid rgba(37,99,235,0.35);
      border-radius: 20px;
      padding: 4px 12px;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: .08em;
      text-transform: uppercase;
      color: #93C5FD;
      margin-bottom: 20px;
    }
    .reset-panel-headline {
      font-size: 28px;
      font-weight: 700;
      line-height: 1.3;
      color: ${tokens.white};
      margin: 0 0 16px;
    }
    .reset-panel-quote {
      font-size: 14px;
      line-height: 1.7;
      color: rgba(255,255,255,0.65);
      border-left: 2px solid ${tokens.accent};
      padding-left: 16px;
      margin: 0 0 40px;
    }
    .reset-panel-stats {
      display: flex;
      gap: 32px;
    }
    .reset-stat-num {
      font-size: 24px;
      font-weight: 700;
      color: ${tokens.white};
    }
    .reset-stat-label {
      font-size: 11px;
      color: rgba(255,255,255,0.5);
      margin-top: 2px;
      letter-spacing: .04em;
    }
    .reset-panel-img {
      width: 100%;
      border-radius: 12px;
      overflow: hidden;
      margin: 32px 0;
    }
    .reset-panel-img img {
      width: 100%;
      display: block;
      object-fit: cover;
      border-radius: 12px;
      opacity: 0.85;
    }
    .reset-panel-brands {
      border-top: 1px solid rgba(255,255,255,0.08);
      padding-top: 24px;
    }
    .reset-brands-label {
      font-size: 11px;
      font-weight: 600;
      letter-spacing: .08em;
      text-transform: uppercase;
      color: rgba(255,255,255,0.35);
      margin-bottom: 16px;
    }
    .reset-brands-grid {
      display: flex;
      gap: 20px;
      flex-wrap: wrap;
      align-items: center;
    }
    .reset-brands-grid img {
      height: 20px;
      filter: brightness(0) invert(1);
      opacity: 0.4;
    }

    /* ── Right / form side ── */
    .reset-form-side {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 48px 32px;
      overflow-y: auto;
    }
    .reset-form-card {
      width: 100%;
      max-width: 400px;
    }
    .reset-form-header {
      margin-bottom: 32px;
    }
    .reset-form-eyebrow {
      font-size: 11px;
      font-weight: 600;
      letter-spacing: .1em;
      text-transform: uppercase;
      color: ${tokens.accent};
      margin-bottom: 8px;
    }
    .reset-form-title {
      font-size: 26px;
      font-weight: 700;
      color: ${tokens.textPrimary};
      margin: 0 0 6px;
    }
    .reset-form-sub {
      font-size: 14px;
      color: ${tokens.textSecondary};
    }

    /* MUI TextField enterprise overrides */
    .reset-form-card .MuiOutlinedInput-root {
      border-radius: 8px !important;
      font-size: 14px !important;
      background: ${tokens.white} !important;
      font-family: 'Inter', system-ui, sans-serif !important;
    }
    .reset-form-card .MuiOutlinedInput-notchedOutline {
      border-color: ${tokens.border} !important;
    }
    .reset-form-card .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
      border-color: #94A3B8 !important;
    }
    .reset-form-card .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: ${tokens.accent} !important;
      border-width: 1.5px !important;
    }
    .reset-form-card .MuiInputLabel-root {
      font-family: 'Inter', system-ui, sans-serif !important;
      font-size: 14px !important;
    }
    .reset-form-card .MuiInputLabel-root.Mui-focused {
      color: ${tokens.accent} !important;
    }

    /* Error banner */
    .reset-error-banner {
      background: #FEF2F2;
      border: 1px solid #FEE2E2;
      color: #991B1B;
      border-radius: 8px;
      padding: 10px 14px;
      font-size: 13px;
      font-weight: 500;
      text-align: center;
    }

    /* Submit button */
    .reset-submit-btn {
      width: 100% !important;
      height: 44px !important;
      background: ${tokens.accent} !important;
      color: #fff !important;
      font-size: 14px !important;
      font-weight: 600 !important;
      letter-spacing: .02em !important;
      border-radius: 8px !important;
      text-transform: none !important;
      box-shadow: none !important;
      font-family: 'Inter', system-ui, sans-serif !important;
      transition: background .15s !important;
    }
    .reset-submit-btn:hover {
      background: ${tokens.accentHover} !important;
    }

    /* Footer / Helper navigation styles */
    .reset-footer {
      margin-top: 28px;
      padding-top: 24px;
      border-top: 1px solid ${tokens.border};
      text-align: right;
      font-size: 13px;
      color: ${tokens.textSecondary};
      font-weight: 500;
    }
    .reset-footer a {
      color: ${tokens.accent};
      font-weight: 700;
      text-decoration: none;
    }
    .reset-footer a:hover { text-decoration: underline; }

    /* Mobile view fallback breakpoints */
    @media (max-width: 768px) {
      .reset-panel { display: none; }
      .reset-form-side { padding: 32px 16px; }
    }
  `}</style>
);

// ─── Component ────────────────────────────────────────────────────────────────
const PasswordresetPage = () => {
    const auth = useSelector(state => state.auth);   
    const profile = useSelector(state => state.auth.profile); 
    const dispatch = useDispatch();
    const [email, setEmail] = useState('');
    const [error, setErrors] = useState(null);
    const [emailerror, setemailErrors] = useState(null);
    const validateerror = document.getElementsByClassName("validateErrors")
    const [visible, SetVisible] = useState(false)
    const [snackOpen, setSnackOpen] = React.useState(false);
    const [alert, setAlert] = useState(false);
    // console.log(validateerror)
    if (validateerror) {
        setTimeout(function () {
            SetVisible(true);
        }, 5000);
    }

    const handleSubmit = () => {
        if (email != '') {
            const values = { 'email': email }
            console.log(values)
            dispatch(doPasswordReset(profile && profile.id,values))
        } else {
            setErrors("Email must be required");
        }
    }

    const emailValidate = () => {
        const emailValidation = !/\S+@\S+\.\S+/.test(email);
        if (emailValidation) {
            setemailErrors("Email address is invalid");
        } else {
            setemailErrors(null);
        }
    };

    const snackhandleClose = () => {
        setAlert(false)
        setSnackOpen(false)
        dispatch(resetNotification())
    };

    useEffect(() => {
        if (auth && auth.notification && auth.notification.messageContent) {
            setAlert(true)
            setSnackOpen(true)
        }
    }, [auth])

    // ── Render ──────────────────────────────────────────────────────────────────
    return (
        <>
            <GlobalStyles />

            {alert &&
                <Snackbar
                    open={snackOpen}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "center"
                    }}
                    autoHideDuration={6000}
                    onClose={snackhandleClose}
                >
                    <Alert
                        severity={auth && auth.notification && auth.notification.messageType === "error" ? "error" : "success"}
                        sx={{ 
                            justifyContent: 'center', 
                            margin: "10px 0",
                            borderRadius: '12px',
                            fontWeight: 600,
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05)'
                        }}
                    >
                        <span>{auth && auth.notification && auth.notification.messageContent}</span>
                    </Alert>
                </Snackbar>
            }

            <div className="reset-root">

                {/* ── Left panel (Mirrored directly from LoginPage) ── */}
                <aside className="reset-panel">
                  <div className="reset-panel-logo">
                    <a href="/">
                      <img src="/assets/images/shippingchimp-logo-white.png" alt="ShippingChimp" />
                    </a>
                  </div>

                  <div>
                    <div className="reset-panel-badge">
                      <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                        <circle cx="4" cy="4" r="4" fill="#60A5FA" />
                      </svg>
                      Trusted by 500+ brands
                    </div>
                    <h2 className="reset-panel-headline">
                      Smarter shipping,<br />faster delivery.
                    </h2>
                    <p className="reset-panel-quote">
                      ShippingChimp beats all the major larger shipping services out there. Their app works great with our Shopify store and everything integrates easily.
                    </p>

                    <div className="reset-panel-img">
                      <img src="/assets/images/register-img.png" alt="ShippingChimp platform" />
                    </div>

                    <div className="reset-panel-stats">
                      <div>
                        <div className="reset-stat-num">99.4%</div>
                        <div className="reset-stat-label">On-time delivery</div>
                      </div>
                      <div>
                        <div className="reset-stat-num">2-min</div>
                        <div className="reset-stat-label">Setup time</div>
                      </div>
                      <div>
                        <div className="reset-stat-num">24/7</div>
                        <div className="reset-stat-label">Support</div>
                      </div>
                    </div>
                  </div>

                  <div className="reset-panel-brands">
                    <p className="reset-brands-label">Trusted by Canadian brands</p>
                    <div className="reset-brands-grid">
                      <img src="/assets/images/tawse.png" alt="Tawse" />
                      <img src="/assets/images/sleepcountry.png" alt="Sleep Country" />
                      <img src="/assets/images/hatley.png" alt="Hatley" />
                      <img src="/assets/images/homedics.png" alt="HoMedics" />
                    </div>
                  </div>
                </aside>

                {/* ── Form side ── */}
                <main className="reset-form-side">
                  <div className="reset-form-card">

                    <div className="reset-form-header">
                      <p className="reset-form-eyebrow">Account Recovery</p>
                      <h1 className="reset-form-title">Reset Password</h1>
                      <p className="reset-form-sub">Enter your verified credentials below to fetch your access parameter link.</p>
                    </div>

                    <Stack
                        component="form"
                        sx={{
                            width: "100%",
                            gap: 2.5
                        }}
                        Validate
                        autoComplete="off"
                    >
                        {/* Global form-level warning feedback component container node */}
                        {!visible && error && (
                            <div className="reset-error-banner validateErrors">{error}</div>
                        )}

                        <TextField 
                            id="email" 
                            label="Email Address" 
                            size="small" 
                            required 
                            variant="outlined"
                            onInput={e => setEmail(e.target.value)} 
                            onBlur={emailValidate} 
                            error={!!emailerror}
                            helperText={emailerror}
                            fullWidth
                            sx={{
                                '& .MuiFormHelperText-root': { mx: 0.5, mt: 0.75, fontWeight: 500 }
                            }}
                        />

                        <Button 
                            variant="contained" 
                            onClick={handleSubmit}
                            className="reset-submit-btn"
                        >
                            Get Password from your email
                        </Button>
                    </Stack>

                    <div className="reset-footer logindefaultview--signup">
                        Return to <a href="/login">Login</a>
                    </div>
                    
                    {(auth.notification.messageContent && auth.notification.messageContent.data) && (
                        <Box sx={{ color: 'red', fontSize: '12.5px', fontWeight: 500, mt: 2, textAlign: 'center' }}>
                            {auth.notification.messageContent.data.msg}
                        </Box>
                    )}

                  </div>
                </main>

            </div>
        </>
    )

}

export default PasswordresetPage;